import { shallowEqual } from 'react-redux';

import { Box, Button, ButtonGroup, Heading, Modal, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_delete,
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_delete,
} from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useSelector } from '../../../selectors/useSelector';

import { GET_LISTING_RECS } from '../../../constants/RecommendationsConstants';
import CostsAndFeesMFCSEvents from '../../../model/ClickstreamEvent/CostsAndFeesMFCSEvents';

type Props = {
  feeId: string;
  setShowDeleteFeeModal: (show: boolean) => void;
  isSingleFamilyFee: boolean;
};

const CostsandFeesDeleteFeeModal = ({ feeId, setShowDeleteFeeModal, isSingleFamilyFee }: Props) => {
  const queryClient = useQueryClient();

  const { currentProperty, listingAlias, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      listingAlias: state.properties.currentProperty.listingAlias,
      user: state.user,
    }),
    shallowEqual,
  );

  const { enqueueToast } = useToast();

  const { mutate: onDeleteMF, isPending: isDeleteMFLoading } =
    useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_delete({
      onSuccess: () => {
        const queryKey = useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get.getKey({
          listingAlias,
        });

        // invalidate the cache to remove the deleted fee
        queryClient.invalidateQueries({ queryKey });

        enqueueToast(<Toast tone="success">The fee was successfully deleted</Toast>);

        setShowDeleteFeeModal(false);

        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      },
      onError: (e) => {
        logError({
          error: e,
          context: {
            message: `Failed to delete multifamily fee for listingAlias: ${listingAlias}, feeGroupId: ${feeId}`,
            details: 'Delete multifamily fee',
          },
        });
        enqueueToast(<Toast tone="critical">The fee couldn&apos;t be deleted. Please try again.</Toast>);
      },
    });

  const { mutate: onDeleteSF, isPending: isDeleteSFLoading } =
    useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_delete({
      onSuccess: () => {
        const queryKey = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get.getKey({
          listingAlias,
        });

        // invalidate the cache to remove the deleted fee
        queryClient.invalidateQueries({ queryKey });

        enqueueToast(<Toast tone="success">The fee was successfully deleted</Toast>);

        setShowDeleteFeeModal(false);

        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      },
      onError: (e) => {
        logError({
          error: e,
          context: {
            message: `Failed to delete fee for listingAlias: ${listingAlias}, costAndFeeId: ${feeId}`,
            details: 'Delete multifamily fee',
          },
        });
        enqueueToast(<Toast tone="critical">The fee couldn&apos;t be deleted. Please try again.</Toast>);
      },
    });

  const handleDeleteClick = () => {
    if (isSingleFamilyFee) {
      onDeleteSF({
        getParams: {
          listingAlias,
          costAndFeeId: feeId,
        },
      });
    } else {
      onDeleteMF({
        getParams: {
          listingAlias,
          feeGroupId: feeId,
        },
      });
      CostsAndFeesMFCSEvents.ConfirmDeleteFee({ currentProperty, user });
    }
  };

  const handleClose = () => {
    setShowDeleteFeeModal(false);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      header={<Heading level={5}>Delete this fee?</Heading>}
      body={
        <div data-testid="CostsandFeesDeleteFeeModal">
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <Text>This will remove the fee from your listing.</Text>
          </Box>
        </div>
      }
      footer={
        <ButtonGroup aria-label="delete fee actions" size="sm">
          <TextButton disabled={isDeleteMFLoading || isDeleteSFLoading} aria-label="cancel" onClick={handleClose}>
            Cancel
          </TextButton>
          <Button
            disabled={isDeleteMFLoading || isDeleteSFLoading}
            aria-label="delete"
            tone="critical"
            emphasis="filled"
            onClick={handleDeleteClick}
            data-eventid={CostsAndFeesMFCSEvents.ConfirmDeleteFee.data_event_id}
          >
            Delete
          </Button>
        </ButtonGroup>
      }
      size="xs"
    />
  );
};

export default CostsandFeesDeleteFeeModal;
